import React, { useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    IconButton,
    useTheme,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import VideocamIcon from '@mui/icons-material/Videocam';
import MicIcon from '@mui/icons-material/Mic';
import CloseIcon from '@mui/icons-material/Close';
import GalleryIcon from '../../assets/gallery.svg?react';
import MicSvgIcon from '../../assets/Mic.svg?react';
import VideoSvgIcon from '../../assets/Video.svg?react';


export type MediaType = 'image' | 'video' | 'audio';

interface MediaSelectionPopupProps {
    open: boolean;
    onClose: () => void;
    mediaType: MediaType;
    onCapture: () => void;
    onLibrary: () => void;
}

const MediaSelectionPopup: React.FC<MediaSelectionPopupProps> = ({
    open,
    onClose,
    mediaType,
    onCapture,
    onLibrary,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [selectedOption, setSelectedOption] = useState<'capture' | 'library' | ''>('');

    const getMediaConfig = () => {
        switch (mediaType) {
            case 'image':
                return {
                    title: 'Foto toevoegen',
                    captureIcon: <CameraAltIcon sx={{ fontSize: '48px' }} />,
                    captureLabel: 'Foto maken',
                    libraryIcon: <GalleryIcon style={{ width: '48px', height: '48px', color: theme.palette.primary.main }} />,
                    libraryLabel: 'Uit galerij',
                };
            case 'video':
                return {
                    title: 'Video toevoegen',
                    captureIcon: <VideocamIcon sx={{ fontSize: '48px' }} />,
                    captureLabel: 'Video opnemen',
                    libraryIcon: <VideoSvgIcon style={{ width: '48px', height: '48px', color: theme.palette.primary.main }} />,
                    libraryLabel: 'Uit galerij',
                };
            case 'audio':
                return {
                    title: 'Audio toevoegen',
                    captureIcon: <MicIcon sx={{ fontSize: '48px' }} />,
                    captureLabel: 'Audio opnemen',
                    libraryIcon: <MicSvgIcon style={{ width: '48px', height: '48px', color: theme.palette.primary.main }} />,
                    libraryLabel: 'Audio uploaden',
                };
            default:
                return {
                    title: 'Media toevoegen',
                    captureIcon: <CameraAltIcon sx={{ fontSize: '48px' }} />,
                    captureLabel: 'Vastleggen',
                    libraryIcon: <GalleryIcon style={{ width: '48px', height: '48px', color: theme.palette.primary.main }} />,
                    libraryLabel: 'Uit galerij',
                };
        }
    };

    const config = getMediaConfig();

    const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedOption(event.target.value as 'capture' | 'library');
    };

    const handleSave = () => {
        if (selectedOption === 'capture') {
            onCapture();
        } else if (selectedOption === 'library') {
            onLibrary();
        }
        onClose();
        setSelectedOption(''); // Reset selection
    };

    const handleClose = () => {
        setSelectedOption(''); // Reset selection on close
        onClose();
    };

    // Use the radio button design for all media types
    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={false}
            fullWidth={false}
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    p: { xs: 1.5, sm: 2.5 },
                    width: { xs: 240, sm: 450 },
                    maxWidth: { xs: 260, sm: 500 },
                    minHeight: 0,
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: { xs: 'flex-start', sm: 'center' },
                    alignItems: 'center',
                    pb: { xs: 0.5, sm: 1 },
                    pt: { xs: 0.5, sm: 0.5 },
                    px: { xs: 2, sm: 1 },
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontFamily: theme.typography.fontFamily,
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        fontSize: 16,
                        textAlign: { xs: 'left', sm: 'center' },
                        width: '100%',
                    }}
                >
                    {config.title}
                </Typography>
                <IconButton
                    onClick={handleClose}
                    sx={{
                        color: theme.palette.grey[500],
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        p: 0.5,
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: { xs: 1, sm: 2 }, pb: { xs: 0.5, sm: 1 }, px: { xs: 1, sm: 2 } }}>
                <FormControl component="fieldset" fullWidth>
                    <RadioGroup
                        value={selectedOption}
                        onChange={handleOptionChange}
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 1, sm: 3 },
                            justifyContent: 'center',
                            alignItems: { xs: 'flex-start', sm: 'center' },
                        }}
                    >
                        <FormControlLabel
                            value="capture"
                            control={
                                <Radio
                                    sx={{
                                        color: theme.palette.primary.main,
                                        mr: 0.2,
                                        p: 0.5,
                                        '&.Mui-checked': {
                                            color: theme.palette.primary.main,
                                        },
                                    }}
                                />
                            }
                            label={
                                <Typography
                                    sx={{
                                        fontFamily: theme.typography.fontFamily,
                                        fontWeight: 500,
                                        color: theme.palette.text.primary,
                                        ml: 0,
                                        fontSize: { xs: 14, sm: 15 },
                                    }}
                                >
                                    {config.captureLabel}
                                </Typography>
                            }
                            sx={{
                                mx: 0,
                                mb: { xs: 0.5, sm: 0 },
                                minHeight: 32,
                                '& .MuiFormControlLabel-label': {
                                    marginLeft: 0,
                                },
                            }}
                        />
                        <FormControlLabel
                            value="library"
                            control={
                                <Radio
                                    sx={{
                                        color: theme.palette.primary.main,
                                        mr: 0.2,
                                        p: 0.5,
                                        '&.Mui-checked': {
                                            color: theme.palette.primary.main,
                                        },
                                    }}
                                />
                            }
                            label={
                                <Typography
                                    sx={{
                                        fontFamily: theme.typography.fontFamily,
                                        fontWeight: 500,
                                        color: theme.palette.text.primary,
                                        ml: 0,
                                        fontSize: { xs: 14, sm: 15 },
                                    }}
                                >
                                    {config.libraryLabel}
                                </Typography>
                            }
                            sx={{
                                mx: 0,
                                mb: 0,
                                minHeight: 32,
                                '& .MuiFormControlLabel-label': {
                                    marginLeft: 0,
                                },
                            }}
                        />
                    </RadioGroup>
                </FormControl>
            </DialogContent>

            <DialogActions sx={{ pt: { xs: 1, sm: 1.5 }, px: { xs: 1, sm: 2 }, pb: { xs: 1, sm: 1.5 }, justifyContent: 'center' }}>
                <Button
                    onClick={handleSave}
                    disabled={!selectedOption}
                    variant="contained"
                    sx={{
                        fontFamily: "Nunito Sans Regular500",
                        fontWeight: 700,
                        textTransform: 'none',
                        borderRadius: 2,
                        px: { xs: 2, sm: 3 },
                        py: { xs: 0.8, sm: 1 },
                        fontSize: { xs: 14, sm: 15 },
                        bgcolor: theme.palette.primary.main,
                        color: 'white',
                        minWidth: { xs: 90, sm: 120 },
                        '&:disabled': {
                            bgcolor: theme.palette.grey[300],
                        },
                    }}
                >
                    Opslaan
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MediaSelectionPopup;
